import { NgIf } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { DynamicFormArrayModel, DynamicFormModel, DynamicTemplateDirective } from '@ng-dynamic-forms/core';
import { DynamicMaterialFormComponent } from '@ng-dynamic-forms/ui-material';
import { CustomDynamicFormService } from '../../custom-dynamic-form.service';
import { getDisabledIds, setModelDisabled } from '../../helpers/disable-model.helper';
import { formElementsInvalid } from '../../helpers/form-elements-invalid.helper';
import { getGroupValue } from '../../helpers/get-group-value.helper';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { DataArrayModel } from './data-array.model';

@Component({
  selector:    'gsm-data-array',
  templateUrl: './data-array.component.html',
  styleUrls:   ['./data-array.component.scss'],
  standalone:  true,
  imports:     [
    DynamicTemplateDirective,
    NgIf,
    MatIconButton,
    MatIcon,
    MatMiniFabButton,
    DynamicMaterialFormComponent
  ]
})
export class DataArrayComponent extends CustomDynamicFormControlComponent<DataArrayModel> implements OnInit {

  private formService = inject(CustomDynamicFormService);

  @ViewChild('form', {static: true}) formComponent: DynamicMaterialFormComponent;

  formModel: DynamicFormModel;
  formGroup: UntypedFormGroup;

  private _formArrayModel: DynamicFormArrayModel;
  private _formArrayControl: UntypedFormArray;

  private _disabledIds: string[] = [];
  private _isUpdating = false;

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  protected onModelChange() {
    this._disabledIds = getDisabledIds(this.model.groupPrototype);

    const json = [
      {
        id:             'formArray',
        type:           'ARRAY',
        groups:         [],
        groupPrototype: this.model.groupPrototype
      }
    ];

    this.formModel = this.formService.fromJSON(json);
    this.formGroup = this.formService.createFormGroup(this.formModel);

    this._formArrayModel = this.formService.findModelById<DynamicFormArrayModel>('formArray', this.formModel);
    this._formArrayControl = this.formService.findControlByModel<UntypedFormArray>(this._formArrayModel, this.formGroup);

    this._updateFormFromValue();

    if (this.model.disabled) {
      setModelDisabled(this.formModel, true);
    }

    this._emitChange();
  }

  protected onValueChange() {
    if (!this._isUpdating) {
      this._updateFormFromValue();
      this.formService.detectChanges(this.formComponent);
      this._emitChange();
    }
  }

  protected onDisabledChange(): void {
    setModelDisabled(this.formModel, this.model.disabled, {excludeIds: this._disabledIds});
    super.onDisabledChange();
  }

  onFormChange(): void {
    if (!this._isUpdating) {
      this._updateValueFromForm();
      this._emitChange();
    }
  }

  private _emitChange() {
    this.group.get(this.model.id)
      .setErrors(this._getErrors());
    this.change.emit(this.model.value);
  }

  private _getErrors(): ValidationErrors {
    const invalid =
      (this.model.required && !(this.model.value && this.model.value.length)) || formElementsInvalid(this.formGroup, this.formModel);
    return invalid ? {dataArray: true} : null;
  }

  addItem() {
    this.formService.addFormArrayGroup(this._formArrayControl, this._formArrayModel);
    this.formService.detectChanges(this.formComponent);
    this._updateValueFromForm();
    this._emitChange();
  }

  removeItem(index: number) {
    this.formService.removeFormArrayGroup(index, this._formArrayControl, this._formArrayModel);
    this.formService.detectChanges(this.formComponent);
    this._updateValueFromForm();
    this._emitChange();
  }

  private _updateFormFromValue() {
    // ignore form change events while this update is happening
    this._isUpdating = true;

    this._formArrayModel.clear();
    this._formArrayControl.clear();

    if (this.model.value) {
      this.model.value.forEach(() => {
        this.formService.addFormArrayGroup(this._formArrayControl, this._formArrayModel);
      });
      this._formArrayControl.patchValue(this.model.value);
    }

    this._isUpdating = false;
  }

  private _updateValueFromForm() {
    this._isUpdating = true;
    this.model.value = this._formArrayModel.groups.map(model => getGroupValue(model.group));
    this._isUpdating = false;
  }
}
